import React from 'react';
import {
  BrowserRouter,
  Link,
  matchPath,
  MemoryRouter,
  Navigate,
  useLocation,
  useNavigate
} from 'react-router-dom';

export { BrowserRouter, Link, MemoryRouter };

const isMatch = (path, pathname, exact = false) => {
  if (!path) return { params: {}, path: '', url: pathname, isExact: true };

  const match = matchPath({ path, end: exact }, pathname);
  if (!match) return null;

  return {
    isExact: match.pathname === pathname,
    params: match.params,
    path: match.pattern.path,
    url: match.pathname
  };
};

export const Redirect = ({ computedMatch, from, push = false, to }) => {
  const location = useLocation();
  const match = computedMatch || (from ? isMatch(from, location.pathname, true) : true);

  return match ? <Navigate replace={!push} to={to} /> : null;
};

const useRouteProps = props => {
  const location = useLocation();
  const navigate = useNavigate();
  const match = props?.match || { isExact: true, params: {}, path: '', url: location.pathname };

  return {
    history: {
      go: delta => navigate(delta),
      goBack: () => navigate(-1),
      push: to => navigate(to),
      replace: to => navigate(to, { replace: true }),
      location
    },
    location,
    match,
    params: match.params
  };
};

const RouteElement = ({ children, component: Component, match, render }) => {
  const routeProps = useRouteProps({ match });

  if (Component) return <Component {...routeProps} />;
  if (render) return render(routeProps);
  if (typeof children === 'function') return children(routeProps);
  return children || null;
};

export const Route = ({ children, component, computedMatch, exact = false, path, render }) => {
  const location = useLocation();
  const match = computedMatch || isMatch(path, location.pathname, exact);

  if (!match) return null;

  return <RouteElement component={component} match={match} render={render}>{children}</RouteElement>;
};

export const Switch = ({ children }) => {
  const location = useLocation();
  let element = null;
  let match = null;

  React.Children.forEach(children, child => {
    if (match || !React.isValidElement(child)) return;

    const { exact = false, from, path } = child.props;
    match = isMatch(path || from, location.pathname, exact);

    if (match) {
      element = React.cloneElement(child, { computedMatch: match });
    }
  });

  return element;
};

export const withRouter = WrappedComponent => {
  const WithRouter = props => {
    const routeProps = useRouteProps(props);
    return <WrappedComponent {...routeProps} {...props} />;
  };

  WithRouter.displayName = `withRouter(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

  return WithRouter;
};
